import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { useLocation, useHistory } from "react-router-dom";
import { NavgationPageLocationKey } from "../../../utils/constants";
import ReactTable from "react-table";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  FormTextarea,
  Row,
} from "shards-react";
import { setAlert } from "../../../redux/actions/alert";
import {
  complete_change_request,
  delete_change_request_note,
  get_change_request,
  save_change_request_note,
} from "../../../redux/actions/subscriber/changeRequest";
import { setDateForTable, setDateForTableInHrMinSec, setInputDate } from "../../../utils/date";
import { parseChangeRequestToSections } from "../../../utils/changeRequestFieldMap";
import CustomModal from "../../../components/common/CustomModal";

const getStatusClass = (status = "") => {
  const n = status.toLowerCase();
  if (n.includes("reject")) return "status-red";
  if (n.includes("request") || n.includes("pending")) return "status-orange";
  return "status-green";
};

const ChangeRequestDetail = ({
  changeRequestDetail,
  currentUser,
  changePageMeta,
  get_change_request,
  save_change_request_note,
  delete_change_request_note,
  complete_change_request,
}) => {
  const location = useLocation();
  const history = useHistory();
  const [refNum, setRefNum] = useState(null);
  const [sections, setSections] = useState([]);
  const [notes, setNotes] = useState([]);
  const [status, setStatus] = useState("");
  const [syncToPayItems, setSyncToPayItems] = useState(false);
  const crClientName = useRef("");
  const crWorkerName = useRef("");
  const [openNoteModal, setOpenNoteModal] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmAlert, setConfirmAlert] = useState({ confirmMsg: "", visible: false, onConfirm: null });
  const cancelConfirmAlert = () => setConfirmAlert(prev => ({ ...prev, visible: false }));
  const [noteText, setNoteText] = useState("");
  const [editNoteRefNum, setEditNoteRefNum] = useState(0);

  useEffect(() => {
    if (!refNum) {
      if (location.state?.refNum) {
        setRefNum(location.state.refNum);
        crClientName.current = location.state.clientName || "";
        crWorkerName.current = location.state.workerName || "";
        get_change_request(location.state.refNum);
      } else {
        const stored = localStorage.getItem(NavgationPageLocationKey.ChangeRequestCurrentAddEdit);
        if (stored) {
          const { refNum: storedRefNum, clientName, workerName } = JSON.parse(stored);
          setRefNum(storedRefNum);
          crClientName.current = clientName;
          crWorkerName.current = workerName;
          get_change_request(storedRefNum);
          history.replace(location.pathname, { refNum: storedRefNum, clientName, workerName });
          localStorage.removeItem(NavgationPageLocationKey.ChangeRequestCurrentAddEdit);
        }
      }
    }
  }, []);

  useEffect(() => {
    if (!changeRequestDetail) return;
    const { status: s, syncProfileDataToPayItems, notes: noteList, userProfile } = changeRequestDetail;
    setStatus(s || "");
    setSyncToPayItems(!!syncProfileDataToPayItems);
    setNotes(noteList || []);

    const _client = crClientName.current?.trim();
    const _worker = crWorkerName.current?.trim();
    const _nameSuffix = [_client, _worker].filter(Boolean).join(' - ');
    changePageMeta(`Change Request - CHR-${refNum}${_nameSuffix ? ` - ${_nameSuffix}` : ''}`);

    const parsed = parseChangeRequestToSections(userProfile);
    setSections(
      parsed.map((section) => ({
        ...section,
        rows: section.rows.map((row) => ({
          ...row,
          approved: row.isApproved || false,
          rejected: row.isRejected || false,
        })),
      }))
    );
  }, [changeRequestDetail]);

  const isRequested = status === "Requested";

  const toggleApprove = (sectionIndex, rowIndex) => {
    setSections((prev) =>
      prev.map((section, si) => {
        if (si !== sectionIndex) return section;
        return {
          ...section,
          rows: section.rows.map((row, ri) => {
            if (ri !== rowIndex) return row;
            const next = !row.approved;
            return { ...row, approved: next, rejected: next ? false : row.rejected };
          }),
        };
      })
    );
  };

  const toggleReject = (sectionIndex, rowIndex) => {
    setSections((prev) =>
      prev.map((section, si) => {
        if (si !== sectionIndex) return section;
        return {
          ...section,
          rows: section.rows.map((row, ri) => {
            if (ri !== rowIndex) return row;
            const next = !row.rejected;
            return { ...row, rejected: next, approved: next ? false : row.approved };
          }),
        };
      })
    );
  };

  const toggleSectionApprove = (sectionIndex, checked) => {
    setSections((prev) =>
      prev.map((section, si) => {
        if (si !== sectionIndex) return section;
        return {
          ...section,
          rows: section.rows.map((row) => ({
            ...row,
            approved: checked,
            rejected: checked ? false : row.rejected,
          })),
        };
      })
    );
  };

  const toggleSectionReject = (sectionIndex, checked) => {
    setSections((prev) =>
      prev.map((section, si) => {
        if (si !== sectionIndex) return section;
        return {
          ...section,
          rows: section.rows.map((row) => ({
            ...row,
            rejected: checked,
            approved: checked ? false : row.approved,
          })),
        };
      })
    );
  };

  const allApproved =
    sections.length > 0 && sections.every((s) => s.rows.every((r) => r.approved));
  const allRejected =
    sections.length > 0 && sections.every((s) => s.rows.every((r) => r.rejected));

  const onApproveAll = (checked) => {
    setSections((prev) =>
      prev.map((section) => ({
        ...section,
        rows: section.rows.map((row) => ({
          ...row,
          approved: checked,
          rejected: checked ? false : row.rejected,
        })),
      }))
    );
  };

  const onRejectAll = (checked) => {
    setSections((prev) =>
      prev.map((section) => ({
        ...section,
        rows: section.rows.map((row) => ({
          ...row,
          rejected: checked,
          approved: checked ? false : row.approved,
        })),
      }))
    );
  };

  const buildApproverList = (sections) =>
    sections
      .filter((s) => s.id.startsWith('approverInfos_'))
      .map((section) => {
        const moduleRow = section.rows.find((r) => r.fieldKey === 'moduleName');
        const moduleName = moduleRow?.newValue || moduleRow?.currentValue || '';
        const { approved, rejected } = section.rows[0];
        return { moduleName, isApproved: approved, isRejected: rejected };
      })
      .filter(({ isApproved, isRejected }) => isApproved || isRejected);

  const buildUserProfile = (sections) => {
    const userProfile = {};

    sections.forEach((section) => {
      if (section.id.startsWith('approverInfos_')) return;

      const hasAction = section.rows.some(({ approved, rejected }) => approved || rejected);
      if (!hasAction) return;

      const arrayMatch = section.id.match(/^(.+)_(\d+)$/);

      if (arrayMatch && arrayMatch[1] === 'payItems' && section.isNew) {
        const firstRow = section.rows[0];
        if (!userProfile.newPayItems) userProfile.newPayItems = [];
        userProfile.newPayItems.push({
          recordId: section.recordId,
          review: {
            isApproved: firstRow ? firstRow.approved : false,
            isRejected: firstRow ? firstRow.rejected : false,
          },
        });
        return;
      }

      section.rows.forEach(({ sectionKey, fieldKey, approved, rejected }) => {
        const approval = { isApproved: approved, isRejected: rejected };
        if (sectionKey === 'root') {
          userProfile[fieldKey] = approval;
        } else if (!arrayMatch) {
          if (!userProfile[sectionKey]) userProfile[sectionKey] = {};
          userProfile[sectionKey][fieldKey] = approval;
        } else {
          const [, arrayKey, idxStr] = arrayMatch;
          const idx = parseInt(idxStr);
          if (!userProfile[arrayKey]) userProfile[arrayKey] = [];
          if (!userProfile[arrayKey][idx]) userProfile[arrayKey][idx] = {};
          userProfile[arrayKey][idx][fieldKey] = approval;

          if (section.recordId != null) {
            userProfile[arrayKey][idx].recordId = section.recordId;
          }
        }
      });

      if (arrayMatch && section.hiddenFields && section.hiddenFields.length > 0) {
        const [, arrayKey, idxStr] = arrayMatch;
        const idx = parseInt(idxStr);
        const allApproved = section.rows.every(row => row.approved === true);
        section.hiddenFields.forEach(fieldKey => {
          if (userProfile[arrayKey] && userProfile[arrayKey][idx]) {
            userProfile[arrayKey][idx][fieldKey] = {
              isApproved: allApproved,
              isRejected: !allApproved,
            };
          }
        });
      }
    });

    Object.keys(userProfile).forEach((key) => {
      if (Array.isArray(userProfile[key])) {
        userProfile[key] = userProfile[key].filter(Boolean);
        if (userProfile[key].length === 0) delete userProfile[key];
      }
    });

    return userProfile;
  };

  const onAcceptRejectChanges = async () => {
    const approverList = buildApproverList(sections);
    const userProfile = {
      ...buildUserProfile(sections),
      ...(approverList.length > 0 ? { approverList } : {}),
    };
    const success = await complete_change_request({
      changeRequestId: parseInt(refNum),
      syncProfileDataToPayItems: syncToPayItems,
      userProfile,
    });
    if (success) {
      get_change_request(refNum);
    }
  };

  const onAddNote = () => {
    setEditNoteRefNum(0);
    setNoteText("");
    setOpenNoteModal(true);
  };

  const onEditNote = (noteRefNum) => {
    const note = notes.find((n) => n.refNum === noteRefNum);
    if (note) {
      setNoteText(note.note);
      setEditNoteRefNum(noteRefNum);
      setOpenNoteModal(true);
    }
  };

  const onSaveNote = async () => {
    if (!noteText.trim()) return;
    const now = new Date();
    const noteDateTime = setInputDate(now) + String(now.getHours()).padStart(2, '0') + String(now.getMinutes()).padStart(2, '0');
    const success = await save_change_request_note({
      changeRequestId: refNum,
      refNum: editNoteRefNum,
      noteDateTime,
      user: currentUser?.displayName || "",
      note: noteText.trim(),
    });
    if (success) {
      setOpenNoteModal(false);
      setNoteText("");
      setEditNoteRefNum(0);
      get_change_request(refNum);
    }
  };

  const onDeleteNote = async (noteRefNum) => {
    const success = await delete_change_request_note({
      changeRequestId: refNum,
      refNum: noteRefNum,
    });
    if (success) {
      setOpenNoteModal(false);
      setConfirmDelete(false);
      get_change_request(refNum);
    }
  };

  const onCancelNote = () => {
    setOpenNoteModal(false);
    setNoteText("");
    setEditNoteRefNum(0);
    setConfirmDelete(false);
  };

  const noteColumns = [
    {
      Header: "Date Time",
      accessor: "noteDateTime",
      maxWidth: 170,
      Cell: (row) => setDateForTableInHrMinSec(row.original.noteDateTime),
    },
    {
      Header: "User",
      accessor: "noteUser",
      maxWidth: 160,
    },
    {
      Header: "Note",
      accessor: "note",
      Cell: (row) => (
        <div className="d-flex justify-content-between align-items-center">
          <span>{row.original.note}</span>
          <img
            src="./icons/edit-square.svg"
            height="17.5"
            width="17.5"
            className="icon-cursor ml-2"
            role="button"
            onClick={() => onEditNote(row.original.refNum)}
          />
        </div>
      ),
    },
  ];

  const getRequestedColumns = (sectionIndex) => {
    const section = sections[sectionIndex];
    const isApproverSection = section?.id?.startsWith('approverInfos_');
    const isDeleteSection = section?.isDelete === true;
    const isNewPayItem = section?.id?.startsWith('payItems_') && section.isNew === true;
    const approverNoChange = isApproverSection && section.rows.every(r => r.currentValue === r.newValue);
    const allSectionApproved = section ? section.rows.every((r) => r.approved) : false;
    const allSectionRejected = section ? section.rows.every((r) => r.rejected) : false;

    const baseColumns = [
      { Header: "Requested by", accessor: "requestedBy", minWidth: 130 },
      { Header: "Field Name", accessor: "fieldName", minWidth: 180 },
      {
        Header: "Current Value",
        accessor: "currentValue",
        minWidth: 160,
        Cell: (row) => {
          const val = row.original.currentValue;
          if (val == null) return "-";
          return row.original.isDate ? (setDateForTable(val) || "-") : String(val);
        },
      },
      {
        id: "arrow",
        Header: "",
        maxWidth: 40,
        sortable: false,
        Cell: () => (
          <i className="material-icons text-muted" style={{ fontSize: "16px" }}>
            arrow_forward
          </i>
        ),
      },
      {
        Header: "New Value",
        accessor: "newValue",
        minWidth: 160,
        Cell: (row) => {
          if (isDeleteSection) return <span className="text-danger font-weight-bold">[Delete]</span>;
          const val = row.original.newValue;
          if (val == null) return "-";
          return row.original.isDate ? (setDateForTable(val) || "-") : String(val);
        },
      },
    ];

    if (approverNoChange) return baseColumns;

    return [
      ...baseColumns,
      {
        Header: (
          <div className="d-flex align-items-center">
            <input
              type="checkbox"
              style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              checked={allSectionApproved}
              onChange={(e) => toggleSectionApprove(sectionIndex, e.target.checked)}
            />
            <span className="ml-1">Approve</span>
          </div>
        ),
        id: "approve",
        maxWidth: 95,
        sortable: false,
        Cell: (row) => (
          <input
            type="checkbox"
            style={{ width: '16px', height: '16px', cursor: isDeleteSection ? 'not-allowed' : 'pointer' }}
            checked={row.original.approved || false}
            onChange={() => {}}
            onClick={isDeleteSection ? undefined : () =>
              (isApproverSection || isNewPayItem)
                ? toggleSectionApprove(sectionIndex, !allSectionApproved)
                : toggleApprove(sectionIndex, row.index)
            }
            disabled={isDeleteSection}
          />
        ),
      },
      {
        Header: (
          <div className="d-flex align-items-center">
            <input
              type="checkbox"
              style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#C41B3C' }}
              checked={allSectionRejected}
              onChange={(e) => toggleSectionReject(sectionIndex, e.target.checked)}
            />
            <span className="ml-1">Reject</span>
          </div>
        ),
        id: "reject",
        maxWidth: 80,
        sortable: false,
        Cell: (row) => (
          <input
            type="checkbox"
            style={{ width: '16px', height: '16px', cursor: isDeleteSection ? 'not-allowed' : 'pointer', accentColor: '#C41B3C' }}
            checked={row.original.rejected || false}
            onChange={() => {}}
            onClick={isDeleteSection ? undefined : () =>
              (isApproverSection || isNewPayItem)
                ? toggleSectionReject(sectionIndex, !allSectionRejected)
                : toggleReject(sectionIndex, row.index)
            }
            disabled={isDeleteSection}
          />
        ),
      },
    ];
  };

  const completedColumns = [
    { Header: "Requested by", accessor: "requestedBy", minWidth: 130 },
    { Header: "Field Name", accessor: "fieldName", minWidth: 180 },
    {
      Header: "Current Value",
      accessor: "currentValue",
      minWidth: 160,
      Cell: (row) => {
        const val = row.original.currentValue;
        if (val == null) return "-";
        return row.original.isDate ? (setDateForTable(val) || "-") : String(val);
      },
    },
    {
      id: "arrow",
      Header: "",
      maxWidth: 40,
      sortable: false,
      Cell: () => (
        <i className="material-icons text-muted" style={{ fontSize: "16px" }}>
          arrow_forward
        </i>
      ),
    },
    {
      Header: "Updated Value",
      accessor: "newValue",
      minWidth: 160,
      Cell: (row) => {
        const val = row.original.newValue;
        if (val == null) return "-";
        return row.original.isDate ? (setDateForTable(val) || "-") : String(val);
      },
    },
    {
      id: "status",
      Header: "Status",
      minWidth: 100,
      sortable: false,
      Cell: (row) => {
        if (row.original.isApproved) {
          return <span className="text-success font-weight-bold">Approved</span>;
        }
        if (row.original.isRejected) {
          return <span className="text-danger font-weight-bold">Rejected</span>;
        }
        const movedMatch = row.original.fieldStatus?.match(/^moved to (\d+)$/i);
        if (movedMatch) {
          const targetRefNum = movedMatch[1];
          return (
            <span>
              {'Moved to '}
              <a
                href="/change-request"
                className="text-primary font-weight-bold"
                onClick={(e) => {
                  e.preventDefault();
                  localStorage.setItem(
                    NavgationPageLocationKey.ChangeRequestCurrentAddEdit,
                    JSON.stringify({ refNum: targetRefNum, clientName: crClientName.current, workerName: crWorkerName.current })
                  );
                  window.open('/change-request', '_blank');
                }}
              >
                {`CHR-${targetRefNum}`}
              </a>
            </span>
          );
        }
        return <span className="text-muted">-</span>;
      },
    },
  ];

  return (
    <Container fluid className="main-content-container px-4 pt-3">
      {confirmAlert.visible && (
        <Alert theme="light" style={{ marginTop: "60px" }}
          className="text-dark fixed-top offset-md-3 offset-lg-2 mb-0 alert alert-danger fade show">
          {confirmAlert.confirmMsg}
          <Button className="mx-2 py-2 btn-normal" size="sm" theme="primary" onClick={confirmAlert.onConfirm}>Yes</Button>
          <Button className="mx-2 py-2" size="sm" theme="white" onClick={cancelConfirmAlert}>Cancel</Button>
        </Alert>
      )}
      {/* <Row noGutters className="page-header py-4">
        <Col>
          <h5 className="mb-0">{pageTitle}</h5>
        </Col>
      </Row> */}

      <Row className="mb-3">
        {/* Notes */}
        <Col md="7">
          <Card small className="h-100">
            <CardBody>
              <div className="mb-2">
                <a
                  href="#!"
                  className="text-primary"
                  onClick={(e) => {
                    e.preventDefault();
                    onAddNote();
                  }}
                >
                  Add Note
                </a>
              </div>

              <ReactTable
                columns={noteColumns}
                data={notes}
                pageSize={notes.length > 0 ? notes.length : 3}
                showPagination={false}
                resizable={false}
                noDataText="No notes"
                className="-striped -highlight"
              />
            </CardBody>
          </Card>
        </Col>

        {/* Status & controls */}
        <Col md="5">
          <div className="d-flex flex-column align-items-end pt-1">
            <div className="d-flex align-items-center mb-3">
              <span className="mr-2" style={{ fontWeight: 600 }}>
                Status:
              </span>
              <span
                className={`badge badge-pill px-3 py-2 ${getStatusClass(status)}`}
                style={{ fontSize: "13px" }}
              >
                {status}
              </span>
              {isRequested && (
                <Button theme="primary" className="ml-3" onClick={() => setConfirmAlert({ confirmMsg: "Are you sure you want to Accept/Reject this change Request?", visible: true, onConfirm: () => { cancelConfirmAlert(); onAcceptRejectChanges(); } })}>
                  Accept/Reject Changes
                </Button>
              )}
            </div>

            {isRequested && (
              <div className="d-flex flex-row align-items-center">
                <label
                  className="d-flex align-items-center mr-4"
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  <input
                    type="checkbox"
                    className="mr-2"
                    style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                    checked={syncToPayItems}
                    onChange={(e) => setSyncToPayItems(e.target.checked)}
                  />
                  Automatically sync Profile data to Pay Items
                </label>
                <label
                  className="d-flex align-items-center mr-4"
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  <input
                    type="checkbox"
                    className="mr-2"
                    style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                    checked={allApproved}
                    onChange={(e) => onApproveAll(e.target.checked)}
                  />
                  Approve All
                </label>
                <label
                  className="d-flex align-items-center"
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  <input
                    type="checkbox"
                    className="mr-2"
                    style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#C41B3C' }}
                    checked={allRejected}
                    onChange={(e) => onRejectAll(e.target.checked)}
                  />
                  Reject All
                </label>
              </div>
            )}
          </div>
        </Col>
      </Row>

      {sections.map((section, sectionIndex) => (
        <Row key={section.id} className="mb-3">
          <Col>
            <Card small>
              <CardHeader className="border-bottom d-flex justify-content-between align-items-center">
                <span className="text-primary font-weight-bold">
                  {section.title}
                  {section.subtitle ? ` - ${section.subtitle}` : ""}
                  {section.isDelete && <span className="text-danger"> - [Delete]</span>}
                </span>
              </CardHeader>
              <CardBody className="p-2">
                <ReactTable
                  columns={
                    isRequested
                      ? getRequestedColumns(sectionIndex)
                      : completedColumns
                  }
                  data={section.rows}
                  pageSize={section.rows.length}
                  showPagination={false}
                  resizable={false}
                  noDataText="No changes"
                  className="-striped -highlight"
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      ))}

      <CustomModal
        toggle={onCancelNote}
        openModal={openNoteModal}
        headerTitle={confirmDelete ? "Delete Note" : editNoteRefNum ? "Edit Note" : "Add Note"}
        footerComponent={
          confirmDelete ? (
            <div className="w-100 d-flex justify-content-end">
              <Button theme="secondary" className="mx-2 px-4" onClick={() => setConfirmDelete(false)}>
                No
              </Button>
              <Button theme="danger" className="mx-2 px-4" onClick={() => onDeleteNote(editNoteRefNum)}>
                Yes, Delete
              </Button>
            </div>
          ) : (
            <div className="w-100 d-flex justify-content-end">
              <Button theme="secondary" className="mx-2 px-4" onClick={onCancelNote}>
                Close
              </Button>
              {editNoteRefNum ? (
                <Button theme="danger" className="mx-2 px-4" onClick={() => setConfirmDelete(true)}>
                  Delete
                </Button>
              ) : null}
              <Button theme="primary" className="mx-2 px-4" onClick={onSaveNote}>
                Save
              </Button>
            </div>
          )
        }
        className="custom-modal"
        footerClasses="d-flex flex-row justify-content-center border-top-0"
      >
        {confirmDelete ? (
          <div className="px-2 py-3">
            <p className="mb-0">Are you sure you want to delete this note?</p>
          </div>
        ) : (
          <div className="px-2 py-1">
            <label className="font-weight-bold mb-1">Note</label>
            <FormTextarea
              rows="4"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Enter note..."
            />
          </div>
        )}
      </CustomModal>
    </Container>
  );
};

const mapStateToProps = (state) => ({
  changeRequestDetail: state.changeRequest.changeRequestDetail,
  currentUser: state.user.user,
});

const mapDispatchToProps = {
  get_change_request,
  save_change_request_note,
  delete_change_request_note,
  complete_change_request,
  setAlert,
};

export default connect(mapStateToProps, mapDispatchToProps)(ChangeRequestDetail);
